import { TFunction } from 'react-i18next'
import { get, indexOf, size } from 'lodash'
import { ColumnsType } from 'antd/lib/table'
import { Avatar, Image, Space, Typography } from 'antd'
import moment from 'moment'

import { UserOutlined } from '@ant-design/icons'

import { IOrderResponse } from '@/screens/Order/interface/order-response.interface'
import { deleteOrderRef } from '@/screens/Order/Pages/Landing/components/OrderTable/OrderTable.service'
import { IMG_PLACE_HOLDER } from '@/constant/common'
import TaskDrawer from './components/TaskDrawer'
import { TASK_STATUS } from '@/screens/Order/Pages/Landing/components/ModalMassUpdate/Task.constant'
import { task_type } from '@/screens/Order/Pages/ViewOrder/TaskTemplate.enum'
import { PriorityStatus } from '@/constant/task'
import { handleTableDefaultSortColumns, toJSON } from '@/utils/common'
export const DATE_FORMAT = 'DD MMM YYYY'

export const MAX_FILE_SIZE = 20 * 1024 * 1024

export enum SORT_TASK {
  priority_status = 'task.priority_status',
  planned_date = 'task.plan_end_date',
  task_name = 'task.name',
  task_value = 'task.value',
  order_number = 'order.number',
  section_name = 'section.name',
  assignee_name = 'user_task.first_name',
  supplier_name = 'order.supplier_name',
  factory_name = 'order.factory_name',
  style_no = 'order.pv_value_str',
  product_description = 'order.pv_product_description_str',
}
export type VALUE_SORT_TASK_TYPE = `${SORT_TASK}`

export const convertToPercentageValue = (value) => {
  try {
    let newValue = value
    newValue = newValue?.replace(/\D/g, '')
    newValue = Math.min(Math.max(parseInt(newValue, 0), 0), 100)
    return newValue || 0
  } catch (error) {
    return 0
  }
}

export const nestedTaskColumns = (
  display: string[],
  t: TFunction<'translation', undefined>,
  options?: any
) => {
  const { refetch, setSelectedTask, selectedTask, queryParams } = options
  const { sort } = queryParams

  const fields: ColumnsType<any> = [
    {
      title: 'TASK NAME',
      dataIndex: 'taskName',
      align: 'left',
      fixed: 'left',
      key: SORT_TASK.task_name,
      defaultSortOrder: handleTableDefaultSortColumns(
        SORT_TASK,
        sort,
        SORT_TASK.task_name
      ),
      sorter: true,
      render: (_, record) => {
        return (
          <Space size="middle">
            <Image
              loading="lazy"
              alt="logo"
              preview={false}
              src={record.orderImageUrl ?? 'error'}
              fallback={IMG_PLACE_HOLDER}
              style={{
                borderRadius: '10px',
                backgroundColor: 'white',
                width: '48px',
                height: '48px',
                objectFit: 'cover',
              }}
            />
            <TaskDrawer
              onClick={setSelectedTask}
              item={record}
              refetch={refetch}
              selectedTask={selectedTask}
              onCancel={() => setSelectedTask(null)}
            />
          </Space>
        )
      },
    },
    {
      title: 'SECTION NAME',
      dataIndex: 'sectionName',
      align: 'left',
      sorter: true,
      key: SORT_TASK.section_name,
      defaultSortOrder: handleTableDefaultSortColumns(
        SORT_TASK,
        sort,
        SORT_TASK.section_name
      ),
    },
    {
      title: t('pbf.table.my_order.order_number'),
      dataIndex: 'orderNo',
      key: SORT_TASK.order_number,
      defaultSortOrder: handleTableDefaultSortColumns(
        SORT_TASK,
        sort,
        SORT_TASK.order_number
      ),
      align: 'left',
      sorter: true,
    },
    {
      title: 'Style no.',
      dataIndex: 'styleNo',
      key: SORT_TASK.style_no,
      defaultSortOrder: handleTableDefaultSortColumns(
        SORT_TASK,
        sort,
        SORT_TASK.style_no
      ),
      align: 'left',
      sorter: true,
      render: (text) => (
        <Typography.Paragraph style={{ margin: 0 }}>
          {text}
        </Typography.Paragraph>
      ),
    },
    {
      title: 'Product Description',
      dataIndex: 'productDescription',
      key: SORT_TASK.product_description,
      defaultSortOrder: handleTableDefaultSortColumns(
        SORT_TASK,
        sort,
        SORT_TASK.product_description
      ),
      align: 'left',
      sorter: true,
      render: (text) => (
        <Typography.Paragraph style={{ margin: 0 }}>
          {text}
        </Typography.Paragraph>
      ),
    },
    {
      title: 'PRIORITY',
      dataIndex: 'priority',
      key: SORT_TASK.priority_status,
      defaultSortOrder: handleTableDefaultSortColumns(
        SORT_TASK,
        sort,
        SORT_TASK.priority_status
      ),
      align: 'left',
      sorter: true,
      render: (status) => {
        switch (status) {
          case PriorityStatus.OVERDUE:
            return <span className="tag red">Overdue</span>
          case PriorityStatus.UPCOMING:
            return <span className="tag yellow">Upcoming</span>
          case PriorityStatus.PLANNED:
            return <span className="tag">Planned</span>
          case PriorityStatus.DONE:
            return <span className="tag green">Done</span>
          case PriorityStatus.NOT_PLANNED:
            return <span className="tag gray">Not Planned</span>
          default:
            return <span className="tag ">{status}</span>
        }
      },
    },
    {
      title: 'PLANNED DATE',
      dataIndex: 'planned_date',
      key: SORT_TASK.planned_date,
      defaultSortOrder: handleTableDefaultSortColumns(
        SORT_TASK,
        sort,
        SORT_TASK.planned_date
      ),
      align: 'center',
      sorter: true,
      render: (text) => {
        return <span>{text}</span>
      },
    },
    {
      title: 'TASK STATUS',
      dataIndex: 'taskStatus',
      align: 'left',
      sorter: true,
      key: SORT_TASK.task_value,
      defaultSortOrder: handleTableDefaultSortColumns(
        SORT_TASK,
        sort,
        SORT_TASK.task_value
      ),
      render: (text, record) => {
        if (record.type === task_type.percentage_progress_type) {
          const percentage = parseInt(record.value) || 0
          if (percentage < 100) {
            return <span className="tag">{percentage}%</span>
          } else {
            return <span className="tag green">{percentage}%</span>
          }
        }
        switch (text) {
          case 'Done':
          case 'Pass':
          case '100%':
          case 'Approved':
            return <span className="tag green">{text}</span>
          case 'Submitted':
            return <span className="tag">{text}</span>
          case 'Fail':
          case 'Rejected':
            return <span className="tag red">{text}</span>
          default:
            return text ? <span className="tag">{text}</span> : '-'
        }
      },
    },
    {
      title: 'ASSIGNEE',
      dataIndex: 'assignee',
      align: 'left',
      sorter: true,
      key: SORT_TASK.assignee_name,
      defaultSortOrder: handleTableDefaultSortColumns(
        SORT_TASK,
        sort,
        SORT_TASK.assignee_name
      ),
      render: (value) => {
        if (!value.name) {
          return <span>-</span>
        }
        return (
          <Space size="middle">
            <Avatar icon={<UserOutlined />} src={value.profileUrl} />
            <span>{value.name}</span>
          </Space>
        )
      },
    },
    {
      title: 'SUPPLIER',
      dataIndex: 'supplier',
      align: 'center',
      sorter: true,
      key: SORT_TASK.supplier_name,
      defaultSortOrder: handleTableDefaultSortColumns(
        SORT_TASK,
        sort,
        SORT_TASK.supplier_name
      ),
    },
    {
      title: 'FACTORY',
      dataIndex: 'factory',
      align: 'center',
      sorter: true,
      key: SORT_TASK.factory_name,
      defaultSortOrder: handleTableDefaultSortColumns(
        SORT_TASK,
        sort,
        SORT_TASK.factory_name
      ),
    },
  ]

  const filteredField = fields?.filter(
    (item) =>
      indexOf(
        [...display, 'progress', 'tasks', 'creator', 'collaborators', 'action'],
        item.key
      ) !== -1
  )

  return display.length ? filteredField : fields
}

export const orderPagination = (
  data: IOrderResponse,
  onPageNumberChange: (page_number) => any
) => {
  return {
    defaultCurrent: data?.page_number ?? 1,
    total: data?.total_record ?? 1,
    showSizeChanger: false,
    pageSize: data?.page_size,
    onChange: (page) => onPageNumberChange(page),
  }
}

export async function deleteOrder(id: string) {
  return await deleteOrderRef(id)
}

export function getKeyByValue(object, value) {
  return Object.keys(object).find(
    (key) => object[key].toLowerCase() === value?.toLowerCase()
  )
}

export function convertUtcToLocalDisplay(date) {
  if (date) {
    return moment(date).format(DATE_FORMAT)
  } else {
    return null
  }
}

export function transformData(response) {
  try {
    if (!size(response)) {
      return []
    }

    const { data } = response.data
    const transformData = data.map((value) => {
      const summaryOrder = get(value, 'summary_order')
      const rawProductDescription = summaryOrder?.pv_product_description_str
      const passProductionDes = toJSON(rawProductDescription)
      const styleId = summaryOrder?.pv_value_str || '-'
      const productDescription = passProductionDes || '-'

      return {
        ...value,
        taskName: value?.name,
        orderImageUrl: summaryOrder?.picture?.url_path,
        orderNo: summaryOrder?.number,
        styleNo: styleId,
        productDescription,
        priority: value.priority_status,
        planned_date: value.plan_start_date
          ? convertUtcToLocalDisplay(value.plan_start_date)
          : '-',
        taskStatus: getKeyByValue(
          get(TASK_STATUS, value.type, {}),
          value.value
        ),
        assignee: {
          name: value?.assignments[0]?.user
            ? value?.assignments[0]?.user?.first_name +
              ' ' +
              value?.assignments[0]?.user?.last_name
            : null,
          profileUrl: value?.assignments[0]?.user?.avatar?.url_path,
          id: value?.assignments[0]?.user?.id,
        },
        sectionName: value.section.name,
        supplier: summaryOrder?.supplier_name,
        factory: summaryOrder?.factory_name,
        isRangeDate: value.is_range_date,
        actualDate: value.actual_date
          ? convertUtcToLocalDisplay(value.actual_date)
          : '-',
        order: summaryOrder,
      }
    })

    return transformData
  } catch (error) {
    return []
  }
}

export function storeLocalTaskPageSize(size: number) {
  localStorage.setItem('task_page_size', size.toString())
}

export function getLocalTaskPageSize(): number {
  const pageSize = localStorage.getItem('task_page_size')
  return pageSize ? Number.parseInt(pageSize) : 10
}
